import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Typography, Space, Tag, Spin } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import WaterLinkService from '../../services/WaterLinkService';

const { Title, Text } = Typography;

const WaterLinkTest = () => {
  const [loading, setLoading] = useState(false);
  const [serviceInfo, setServiceInfo] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [error, setError] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);

  const checkConnection = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Vérifier le service
      const service = await WaterLinkService.checkService();
      setServiceInfo(service);
      
      // Vérifier le statut de l'appareil
      const status = await WaterLinkService.getStatus();
      setDeviceStatus(status);
      
      setLastCheck(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkConnection();
  }, []);

  const getStatusIcon = (status) => {
    if (status === 5) return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    if (status === 0) return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    return <CloseCircleOutlined style={{ color: '#faad14' }} />;
  };

  const getStatusText = (status) => {
    return WaterLinkService.getStatusText(status);
  };

  const getStatusColor = (status) => {
    return WaterLinkService.getStatusColor(status);
  };

  return (
    <Card title="Test de connectivité WaterLink Connect" size="small">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Button 
            type="primary" 
            icon={<ReloadOutlined />} 
            onClick={checkConnection}
            loading={loading}
          >
            Tester la connexion
          </Button>
          {lastCheck && (
            <Text type="secondary" style={{ marginLeft: 16 }}>
              Dernière vérification : {lastCheck.toLocaleTimeString()}
            </Text>
          )}
        </div>

        {loading && (
          <div style={{ textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Vérification de la connexion...</Text>
            </div>
          </div>
        )}

        {error && (
          <Alert
            message="Erreur de connexion"
            description={error}
            type="error"
            showIcon
          />
        )}

        {serviceInfo && (
          <Card size="small" title="Informations du service">
            <Space direction="vertical" size="small">
              <div>
                <Text strong>Version : </Text>
                <Text>{serviceInfo.version}</Text>
              </div>
              <div>
                <Text strong>Documentation : </Text>
                <Text>{serviceInfo.documentation}</Text>
              </div>
            </Space>
          </Card>
        )}

        {deviceStatus && (
          <Card size="small" title="État de l'appareil">
            <Space direction="vertical" size="small">
              <div>
                <Text strong>Nom : </Text>
                <Text>{deviceStatus.Name || 'Non connecté'}</Text>
              </div>
              <div>
                <Text strong>Statut : </Text>
                <Space>
                  {getStatusIcon(deviceStatus.Status)}
                  <Tag color={getStatusColor(deviceStatus.Status)}>
                    {getStatusText(deviceStatus.Status)}
                  </Tag>
                </Space>
              </div>
              <div>
                <Text strong>Firmware actuel : </Text>
                <Text>{deviceStatus.CurrentFirmwareString || 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Firmware disponible : </Text>
                <Text>{deviceStatus.AvailFirmwareString || 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Progrès : </Text>
                <Text>{deviceStatus.Progress}%</Text>
              </div>
              <div>
                <Text strong>Capacités : </Text>
                <Space wrap>
                  {deviceStatus.CanDoWatertest && <Tag color="green">Test d'eau</Tag>}
                  {deviceStatus.CanGetLog && <Tag color="blue">Logs</Tag>}
                  {deviceStatus.CanDoUpdate && <Tag color="orange">Mise à jour</Tag>}
                  {deviceStatus.CanDoCalibrate && <Tag color="purple">Calibration</Tag>}
                </Space>
              </div>
            </Space>
          </Card>
        )}

        {!loading && !error && serviceInfo && deviceStatus && (
          <Alert
            message="Connexion établie"
            description="Le service WaterLink Connect est accessible et l'appareil est détecté."
            type="success"
            showIcon
          />
        )}
      </Space>
    </Card>
  );
};

export default WaterLinkTest;

